import React, { useLayoutEffect, useRef, useState } from 'react';

import { computeAutoFitFontPx } from './autoFitTitleFontSize';

/** Props for {@link AutoFitTitle}. */
interface AutoFitTitleProps {
  text: string;
  className?: string;
  maxFontPx?: number;
  minFontPx?: number;
  onClick?: () => void;
}

/**
 * Single-line title whose font size is reduced as needed so the text fits the container width (resize-aware).
 */
export const AutoFitTitle: React.FC<AutoFitTitleProps> = ({
  text,
  className,
  maxFontPx = 48,
  minFontPx = 10,
  onClick,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [fontPx, setFontPx] = useState(maxFontPx);

  useLayoutEffect(() => {
    const fitText = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const availableWidth = container.clientWidth;
      if (availableWidth <= 0) return;

      const nextFontPx = computeAutoFitFontPx(
        availableWidth,
        minFontPx,
        maxFontPx,
        (fontPx) => {
          measure.style.fontSize = `${fontPx}px`;
          return measure.scrollWidth;
        }
      );

      setFontPx(nextFontPx);
    };

    fitText();
    const resizeObserver = new ResizeObserver(fitText);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [text, maxFontPx, minFontPx]);

  return (
    <>
      <span
        ref={containerRef}
        className={className}
        style={{ fontSize: `${fontPx}px` }}
        onClick={onClick}
      >
        {text}
      </span>
      <span ref={measureRef} className={`${className ?? ''} compact-edit-title-measure`}>
        {text}
      </span>
    </>
  );
};
